import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Image as ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getProjects, resolveMedia } from "@/lib/api";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    getProjects()
      .then((r) => setProjects(r.data || []))
      .catch(() => {});
  }, []);

  const normalizedProjects = useMemo(() => {
    return (projects || []).map((project) => {
      const images = (project.images || []).filter(Boolean);
      const fallback = project.after_image || project.before_image;
      const gallery = images.length ? images : fallback ? [fallback] : [];

      return {
        ...project,
        gallery,
      };
    });
  }, [projects]);

  const openLightbox = (projectIndex, imageIndex) => {
    setLightbox({ projectIndex, imageIndex });
    setZoom(1);
  };

  const closeLightbox = () => {
    setLightbox(null);
    setZoom(1);
  };

  const currentProject =
    lightbox !== null ? normalizedProjects[lightbox.projectIndex] : null;

  const currentGallery = currentProject?.gallery || [];

  const currentImage =
    lightbox !== null && currentGallery.length
      ? currentGallery[lightbox.imageIndex]
      : null;

  const goPrev = () => {
    if (!currentGallery.length) return;

    setLightbox((prev) => ({
      ...prev,
      imageIndex:
        prev.imageIndex === 0 ? currentGallery.length - 1 : prev.imageIndex - 1,
    }));
    setZoom(1);
  };

  const goNext = () => {
    if (!currentGallery.length) return;

    setLightbox((prev) => ({
      ...prev,
      imageIndex:
        prev.imageIndex === currentGallery.length - 1 ? 0 : prev.imageIndex + 1,
    }));
    setZoom(1);
  };

  const zoomIn = () => {
    setZoom((value) => Math.min(value + 0.25, 3));
  };

  const zoomOut = () => {
    setZoom((value) => Math.max(value - 0.25, 1));
  };

  const resetZoom = () => {
    setZoom(1);
  };

  useEffect(() => {
    if (!lightbox) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowLeft") goPrev();
      if (event.key === "ArrowRight") goNext();
      if (event.key === "+" || event.key === "=") zoomIn();
      if (event.key === "-") zoomOut();
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [lightbox, currentGallery.length]);

  return (
    <main data-testid="projects-page" className="pt-20 lg:pt-24 bg-construtop-texture">
      <section className="bg-zinc-950 py-20 lg:py-28 section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-emerald-400 text-sm font-bold uppercase tracking-[0.2em] mb-3"
          >
            Proyectos Realizados
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl text-white mb-4"
          >
            Galería de obras y aplicaciones
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-zinc-400 max-w-2xl text-base"
          >
            Imágenes de proyectos organizadas en formato galería. Cada proyecto
            queda preparado para que el administrador agregue la especificación
            técnica cuando el cliente la entregue.
          </motion.p>
        </div>
      </section>

      <section className="section-spacing section-padding">
        <div className="max-w-7xl mx-auto space-y-14">
          {normalizedProjects.map((project, projectIndex) => {
            const gallery = project.gallery || [];

            return (
              <motion.article
                key={project.id || projectIndex}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                data-testid={`project-card-${projectIndex}`}
                className="bg-white border border-zinc-200 overflow-hidden"
              >
                <div className="p-6 lg:p-8 border-b border-zinc-100 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
                  <div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {project.category && (
                        <Badge
                          variant="secondary"
                          className="rounded-none text-xs"
                        >
                          {project.category}
                        </Badge>
                      )}

                      {project.year && (
                        <Badge
                          variant="outline"
                          className="rounded-none text-xs"
                        >
                          {project.year}
                        </Badge>
                      )}
                    </div>

                    <h3 className="font-heading text-2xl text-zinc-900">
                      {project.title}
                    </h3>

                    {project.location && (
                      <div className="flex items-center gap-1 text-zinc-500 text-sm mt-1">
                        <MapPin size={14} />
                        {project.location}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider">
                    <ImageIcon size={16} className="text-emerald-700" />
                    {gallery.length} imágenes
                  </div>
                </div>

                {project.description && (
                  <p className="px-6 lg:px-8 pt-6 text-zinc-600 text-sm leading-relaxed max-w-4xl">
                    {project.description}
                  </p>
                )}

                <div className="p-6 lg:p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {gallery.map((img, imageIndex) => (
                    <button
                      type="button"
                      key={`${project.id || projectIndex}-${imageIndex}`}
                      onClick={() => openLightbox(projectIndex, imageIndex)}
                      className="group bg-zinc-50 border border-zinc-100 overflow-hidden min-h-[220px] flex items-center justify-center cursor-zoom-in text-left"
                      aria-label={`Abrir imagen ${imageIndex + 1} de ${project.title}`}
                    >
                      <div className="relative w-full h-[260px] overflow-hidden">
                        <img
                          src={resolveMedia(img)}
                          alt={`${project.title} ${imageIndex + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />

                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300 flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-xs font-bold uppercase tracking-[0.2em] border border-white/60 px-4 py-2">
                            Ver imagen
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}

                  {gallery.length === 0 && (
                    <div className="col-span-full h-56 bg-zinc-100 flex items-center justify-center text-zinc-400 text-sm">
                      Imágenes pendientes de carga
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}

          {normalizedProjects.length === 0 && (
            <div className="text-center py-20 text-zinc-500">
              Proyectos en proceso de carga.
            </div>
          )}
        </div>
      </section>

      {lightbox && currentImage && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center px-4 py-6"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Cerrar galería"
          >
            <X size={22} />
          </button>

          {currentGallery.length > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                aria-label="Imagen anterior"
              >
                <ChevronLeft size={28} />
              </button>

              <button
                type="button"
                onClick={goNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                aria-label="Imagen siguiente"
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}

          <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
            <button
              type="button"
              onClick={zoomOut}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Reducir zoom"
            >
              <ZoomOut size={19} />
            </button>

            <button
              type="button"
              onClick={resetZoom}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Restablecer zoom"
            >
              <RotateCcw size={18} />
            </button>

            <button
              type="button"
              onClick={zoomIn}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Aumentar zoom"
            >
              <ZoomIn size={19} />
            </button>

            <span className="hidden sm:inline-flex text-white/70 text-xs font-bold tracking-wider ml-2">
              {Math.round(zoom * 100)}%
            </span>
          </div>

          <div className="w-full max-w-6xl max-h-[82vh] overflow-auto flex items-center justify-center">
            <img
              src={resolveMedia(currentImage)}
              alt={`${currentProject?.title || "Proyecto"} ${lightbox.imageIndex + 1}`}
              className="max-w-full max-h-[82vh] object-contain transition-transform duration-300"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: "center center",
              }}
            />
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 text-center text-white">
            <p className="font-heading text-lg">
              {currentProject?.title}
            </p>

            {currentGallery.length > 1 && (
              <p className="text-xs text-white/60 mt-1">
                {lightbox.imageIndex + 1} / {currentGallery.length}
              </p>
            )}
          </div>
        </div>
      )}
    </main>
  );
}